import { Icon, Text } from "@zapier/design-system";
import { useAtomValue } from "jotai";
import {
  Actor,
  DefaultNodeData,
  nodeAtomFamily,
  Service,
  ZapNodeData,
} from "@zapier/canvas-state";
import { Node } from "reactflow";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";

import { Notes } from "../Notes";
import { ActorList } from "../NodeSidebar/ActorList";
import { AppList } from "../NodeSidebar/AppList";
import { ZapPreviewContainer } from "../NodeSidebar/ZapPreviewContainer";
import { Sidebar } from "../Sidebar";

import styles from "./ViewOnlyNodeSidebar.module.css";
import { ACTOR_TYPEAHEAD_LABEL } from "@zapier/canvas-constants";

export function ViewOnlyNodeSidebar({ nodeId }: { nodeId: string }) {
  const node = useAtomValue(nodeAtomFamily(nodeId));
  if (!node) {
    return null;
  }

  // For now we don't show a sidebar for paths because there is nothing novel
  // to show. If we want to show notes or other information for paths in the
  // future, we can remove this check.
  if (node.type === "path") {
    return null;
  }

  if (node.type === "zap" && node?.data.zapId) {
    return <ViewOnlyZapNodeSidebar node={node} />;
  }

  if (
    !node?.data.app &&
    !node?.data.actors &&
    isRemirrorJSONEmpty(node?.data.notesJson)
  ) {
    return (
      <Sidebar heading={node?.data.label}>
        <span className={styles.emptyState}>
          <span className={styles.iconWrapper}>
            <Icon name="fileFile" color="GrayWarm6" size={30} />
          </span>
          <Text type="SmallPrint1">
            This step doesn&apos;t have any information.
          </Text>
        </span>
      </Sidebar>
    );
  }

  const nodeData = node.data as DefaultNodeData;

  return (
    <Sidebar heading={node?.data.label}>
      <div className={styles.wrapper}>
        <ViewOnlyApps
          services={nodeData.services}
          nodeId={nodeId}
          node={node}
        />
        <ViewOnlyActors actors={nodeData.actors} nodeId={nodeId} />
        {!isRemirrorJSONEmpty(nodeData.notesJson) && (
          <span>
            <Notes notesJson={nodeData.notesJson} nodeId={node.id} />
          </span>
        )}
      </div>
    </Sidebar>
  );
}

function ViewOnlyApps({
  nodeId,
  services,
  node,
}: {
  nodeId: string;
  services: Service[] | undefined;
  node: Node;
}) {
  if (!services || services.length === 0) {
    return null;
  }

  return (
    <span>
      <Text type="SmallPrint2">App(s)</Text>
      <AppList nodeId={nodeId} services={services} node={node} />
    </span>
  );
}

function ViewOnlyActors({
  nodeId,
  actors,
}: {
  nodeId: string;
  actors: Actor[] | undefined;
}) {
  if (!actors || actors.length === 0) {
    return null;
  }
  return (
    <span>
      <Text type="SmallPrint2">{ACTOR_TYPEAHEAD_LABEL}</Text>
      <ActorList nodeId={nodeId} actors={actors} />
    </span>
  );
}

function ViewOnlyZapNodeSidebar({ node }: { node: Node<ZapNodeData> }) {
  const zapId = node?.data.zapId;
  if (!zapId) {
    return null;
  }
  const nodeId = node.id;
  return (
    <Sidebar heading={node.data.label}>
      <div className={styles.wrapper}>
        <ZapPreviewContainer zapId={zapId} />
        <ViewOnlyActors nodeId={nodeId} actors={node.data.actors} />
        {!isRemirrorJSONEmpty(node.data.notesJson) && (
          <span>
            <Notes nodeId={nodeId} notesJson={node.data.notesJson} />
          </span>
        )}
      </div>
    </Sidebar>
  );
}
